import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Heart, Share2, MapPin, Scale, Phone } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { usePropertyComparison } from '@/hooks/usePropertyComparison';

// WhatsApp icon component
const WhatsAppIcon = ({
  className
}: {
  className?: string;
}) => <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
  </svg>;

interface ListingDetailActionsProps {
  listing: any;
  onToggleWishlist: () => void;
  isInWishlist: boolean;
  onScheduleVisit: () => void;
  onOpenChat: () => void;
  onAddToComparison?: (listing: any) => void;
}

const ListingDetailActions = ({
  listing,
  onToggleWishlist,
  isInWishlist,
  onAddToComparison
}: ListingDetailActionsProps) => {
  const {
    user,
    profile
  } = useAuth();
  const {
    t
  } = useLanguage();
  const navigate = useNavigate();
  const [wishlistLoading, setWishlistLoading] = useState(false);
  const {
    addToComparison
  } = usePropertyComparison();

  const handlePhoneCall = () => {
    // Check if user is verified by admin or has KYC documents
    const isVerified = profile?.kyc_status === 'VERIFIED';
    const hasKYCDocuments = profile?.id_front_url && profile?.id_back_url;
    
    if (!isVerified && !hasKYCDocuments) {
      toast({
        title: t('verification_required_contact', 'التحقق مطلوب للتواصل', 'Verification required for contact'),
        description: t('upload_or_verify_id', 'يرجى رفع بطاقة الهوية أو انتظار التحقق من الإدارة', 'Please upload your ID or wait for admin verification'),
        variant: "destructive"
      });
      navigate('/account?kyc_error=required&redirect=contact');
      return;
    }

    if (listing.profiles?.phone) {
      // Clean the phone number for the tel: link
      const phoneNumber = listing.profiles.phone.replace(/[^\d+]/g, '');
      window.location.href = `tel:${phoneNumber}`;
    } else {
      toast({
        title: t('phone_not_available', 'رقم الهاتف غير متوفر', 'Phone number not available'),
        description: t('contact_owner_directly', 'يرجى التواصل مع المالك مباشرة', 'Please contact the owner directly'),
        variant: "destructive"
      });
    }
  };

  const handleWhatsAppContact = () => {
    // Check if user is verified by admin or has KYC documents
    const isVerified = profile?.kyc_status === 'VERIFIED';
    const hasKYCDocuments = profile?.id_front_url && profile?.id_back_url;
    
    if (!isVerified && !hasKYCDocuments) {
      toast({
        title: t('verification_required_contact', 'التحقق مطلوب للتواصل', 'Verification required for contact'),
        description: t('upload_or_verify_id', 'يرجى رفع بطاقة الهوية أو انتظار التحقق من الإدارة', 'Please upload your ID or wait for admin verification'),
        variant: "destructive"
      });
      navigate('/account?kyc_error=required&redirect=contact');
      return;
    }

    // Check if WhatsApp link exists and is valid
    if (listing.whatsapp_link && listing.whatsapp_link.trim() !== '') {
      // Ensure the link starts with https://wa.me/ or https://api.whatsapp.com/
      let whatsappUrl = listing.whatsapp_link;
      if (!whatsappUrl.startsWith('http')) {
        // If it's just a phone number, create the WhatsApp URL
        const phoneNumber = whatsappUrl.replace(/[^\d]/g, ''); // Remove non-digits
        whatsappUrl = `https://wa.me/${phoneNumber}`;
      }
      
      window.open(whatsappUrl, '_blank');
    } else if (listing.profiles?.phone) {
      // Try to create WhatsApp link from owner's phone if available
      const phoneNumber = listing.profiles.phone.replace(/[^\d]/g, ''); // Remove non-digits
      if (phoneNumber) {
        const whatsappUrl = `https://wa.me/${phoneNumber}`;
        window.open(whatsappUrl, '_blank');
      } else {
        toast({
          title: t('whatsapp_not_available', 'رابط واتساب غير متوفر', 'WhatsApp contact not available'),
          description: t('contact_owner_directly', 'يرجى التواصل مع المالك مباشرة', 'Please contact the owner directly'),
          variant: "destructive"
        });
      }
    } else {
      // No WhatsApp link or phone number available
      toast({
        title: t('whatsapp_not_available', 'رابط واتساب غير متوفر', 'WhatsApp contact not available'),
        description: t('contact_owner_directly', 'يرجى التواصل مع المالك مباشرة', 'Please contact the owner directly'),
        variant: "destructive"
      });
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: listing.title,
          text: listing.description,
          url: url
        });
      } catch (err) {
        // User cancelled sharing or error occurred, fallback to clipboard
        await navigator.clipboard.writeText(url);
        toast({
          title: t('link_copied', 'تم نسخ الرابط', 'Link copied to clipboard')
        });
      }
    } else {
      // Fallback to copying to clipboard
      await navigator.clipboard.writeText(url);
      toast({
        title: t('link_copied', 'تم نسخ الرابط', 'Link copied to clipboard')
      });
    }
  };

  const handleWishlistToggle = async () => {
    if (!user) {
      toast({
        title: t('sign_in_required', 'يجب تسجيل الدخول أولاً', 'Please sign in first'),
        variant: "destructive"
      });
      return;
    }
    try {
      setWishlistLoading(true);
      if (isInWishlist) {
        // Remove from wishlist
        const {
          error
        } = await supabase.from('wishlists').delete().eq('user_id', user.id).eq('listing_id', listing.id);
        if (error) {
          console.error('Error removing from wishlist:', error);
          toast({
            title: t('error_occurred', 'حدث خطأ', 'An error occurred'),
            variant: "destructive"
          });
          return;
        }
        toast({
          title: t('removed_from_wishlist', 'تم إزالة العقار من المفضلة', 'Removed from wishlist')
        });
      } else {
        // Add to wishlist
        const {
          error
        } = await supabase.from('wishlists').insert({
          user_id: user.id,
          listing_id: listing.id
        });
        if (error) {
          console.error('Error adding to wishlist:', error);
          toast({
            title: t('error_occurred', 'حدث خطأ', 'An error occurred'),
            variant: "destructive"
          });
          return;
        }
        toast({
          title: t('added_to_wishlist', 'تم إضافة العقار للمفضلة', 'Added to wishlist')
        });
      }

      // Call the parent's toggle function to update the state
      onToggleWishlist();
    } catch (error) {
      console.error('Error toggling wishlist:', error);
      toast({
        title: t('error_occurred', 'حدث خطأ', 'An error occurred'),
        variant: "destructive"
      });
    } finally {
      setWishlistLoading(false);
    }
  };

  const handleAddToCompare = () => {
    if (addToComparison) {
      // Transform listing data to match the expected Property interface
      const propertyForComparison = {
        id: listing.id,
        title: listing.title,
        price_egp: listing.price || listing.price_egp,
        deposit_egp: listing.deposit || listing.deposit_egp,
        distance_km: listing.distance || listing.distance_km,
        unit_type: listing.unitType || listing.unit_type,
        furnished: listing.furnished,
        verified: listing.verified,
        photos: listing.images || listing.photos || ['/placeholder.svg'],
        amenities: listing.amenities || [],
        available_from: listing.availableFrom || listing.available_from,
        lease_min_months: listing.leaseMinMonths || listing.lease_min_months,
        gender_rules: listing.genderRules || listing.gender_rules,
        universities: {
          name_en: listing.university,
          name_ar: listing.university
        }
      };
      addToComparison(propertyForComparison);
      toast({
        title: t('added_to_comparison', 'تم إضافة العقار للمقارنة', 'Added to comparison'),
        description: t('view_comparison', 'يمكنك عرض المقارنة من القائمة', 'You can view the comparison from the menu')
      });
    }
  };

  const isOwner = user && listing.owner_id === user.id;
  if (isOwner) {
    return <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Badge variant="outline">
              {t('your_listing', 'إعلانك', 'Your Listing')}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button onClick={handleShare} variant="outline" className="w-full">
            <Share2 className="h-4 w-4 mr-2" />
            {t('share_listing', 'مشاركة الإعلان', 'Share Listing')}
          </Button>
        </CardContent>
      </Card>;
  }
  return <Card>
      <CardContent className="space-y-3 pt-6">
        {/* WhatsApp Contact with KYC Check - Made larger */}
        <Button onClick={handleWhatsAppContact} className="w-full bg-green-600 hover:bg-green-700 min-h-[52px] px-4 py-3 text-base font-medium">
          <WhatsAppIcon className="h-5 w-5 mr-2 flex-shrink-0" />
          <span className="whitespace-normal text-center leading-tight">
            {t('contact_whatsapp', 'تواصل عبر واتساب', 'Contact via WhatsApp')}
          </span>
        </Button>

        {/* Phone Call */}
        {listing.profiles?.phone && (
          <Button 
            onClick={handlePhoneCall} 
            variant="outline" 
            className="w-full bg-blue-600 hover:bg-blue-700 text-white border-blue-600 hover:border-blue-700 min-h-[44px] px-4 py-3"
          >
            <Phone className="h-4 w-4 mr-2 flex-shrink-0" />
            <span className="truncate">
              {t('call_owner', 'اتصل بالمالك', 'Call Owner')}
            </span>
          </Button>
        )}

        {/* Location */}
        {listing.google_maps_link && <Button onClick={() => window.open(listing.google_maps_link, '_blank')} variant="outline" className="w-full min-h-[44px] px-4 py-3">
            <MapPin className="h-4 w-4 mr-2 flex-shrink-0" />
            <span className="truncate">
              {t('view_on_map', 'عرض على الخريطة', 'View on Map')}
            </span>
          </Button>}

        {/* Wishlist */}
        <Button onClick={handleWishlistToggle} variant="outline" className="w-full min-h-[44px] px-4 py-3" disabled={wishlistLoading}>
          <Heart className={`h-4 w-4 mr-2 flex-shrink-0 ${isInWishlist ? 'fill-current text-red-500' : ''}`} />
          <span className="truncate">
            {isInWishlist ? t('remove_from_wishlist', 'إزالة من المفضلة', 'Remove from Wishlist') : t('add_to_wishlist', 'إضافة للمفضلة', 'Add to Wishlist')}
          </span>
        </Button>

        {/* Share */}
        <Button onClick={handleShare} variant="outline" className="w-full min-h-[44px] px-4 py-3">
          <Share2 className="h-4 w-4 mr-2 flex-shrink-0" />
          <span className="truncate">
            {t('share_listing', 'مشاركة الإعلان', 'Share Listing')}
          </span>
        </Button>

        {/* Add to Compare */}
        <Button onClick={handleAddToCompare} variant="outline" className="w-full min-h-[44px] px-4 py-3">
          <Scale className="h-4 w-4 mr-2 flex-shrink-0" />
          <span className="truncate">
            {t('add_to_compare', 'إضافة للمقارنة', 'Add to Compare')}
          </span>
        </Button>
      </CardContent>
    </Card>;
};
export default ListingDetailActions;
